const emptyTournamentEntity = { isVerified: false, verifiedAt: null };

const defaultScoreState = (tournamentId, teamId) => ({
  id: null,
  tournamentId,
  entityType: "scores",
  entityId: teamId,
  isVerified: false,
  verifiedBy: null,
  verifiedAt: null,
  unverifiedBy: null,
  unverifiedAt: null,
  createdAt: null,
  updatedAt: null,
});

export const requiredAveragesAreVerified = (verificationStatus) => {
  const averages = verificationStatus?.averages || [];
  const activeBowlerIds = verificationStatus?.activeBowlerIds;
  if (!Array.isArray(activeBowlerIds)) {
    return averages.every((state) => state.isVerified);
  }
  const required = new Set(activeBowlerIds.map(String));
  return averages
    .filter((state) => required.has(String(state.entityId)))
    .every((state) => state.isVerified);
};

// Score verify is per team. Other squads' averages do not belong in this check.
// This team's bowlers are checked from the team roster, not from every average row.
export const canVerifyTeamScores = (verificationStatus, teamId) => {
  if (!teamId) return false;
  if (
    !verificationStatus ||
    !Array.isArray(verificationStatus.scores) ||
    !Array.isArray(verificationStatus.averages)
  ) {
    return false;
  }

  const teamVerified = verificationStatus.scores.find(
    (state) => state.entityId === teamId
  )?.isVerified;

  return !teamVerified;
};

export const buildVerificationStatus = ({
  tournamentId,
  states = [],
  activeTeamIds = [],
  activeBowlerIds = [],
  canVerifyPayouts = false,
  canVerifyResults = false,
  canVerifyTournament = false,
  unverificationPathAverages = [],
  unverificationPathScores = [],
  unverificationPathPayouts = [],
  unverificationPathResults = [],
}) => {
  const rows = states || [];
  const averages = rows.filter((state) => state.entityType === "averages");
  const scores = (activeTeamIds || []).map((teamId) => {
    const existing = rows.find(
      (state) => state.entityType === "scores" && state.entityId === teamId
    );
    return existing || defaultScoreState(tournamentId, teamId);
  });
  const payouts =
    rows.find((state) => state.entityType === "payouts") || emptyTournamentEntity;
  const results =
    rows.find((state) => state.entityType === "results") || emptyTournamentEntity;
  const tournament =
    rows.find((state) => state.entityType === "tournament") ||
    emptyTournamentEntity;

  return {
    averages,
    scores,
    payouts,
    results,
    tournament,
    activeBowlerIds: activeBowlerIds || [],
    computed: {
      canVerifyPayouts: Boolean(canVerifyPayouts),
      canVerifyResults: Boolean(canVerifyResults),
      canVerifyTournament: Boolean(canVerifyTournament),
    },
    unverificationPaths: {
      averages: unverificationPathAverages || [],
      scores: unverificationPathScores || [],
      payouts: unverificationPathPayouts || [],
      results: unverificationPathResults || [],
    },
  };
};

const rpcOrDefault = async (supabase, name, params, fallback) => {
  const { data, error } = await supabase.rpc(name, params);
  if (error) {
    console.warn(`RPC ${name} failed:`, error);
    return fallback;
  }
  return data ?? fallback;
};

export const loadTournamentVerificationFromTables = async (
  supabase,
  tournamentId
) => {
  const [statesResult, teamsResult, payouts, results, tournament, avgPath, scoresPath, payoutsPath, resultsPath] =
    await Promise.all([
      supabase
        .from("verificationStates")
        .select("*")
        .eq("tournamentId", tournamentId),
      supabase
        .from("teams")
        .select("id, teamassignments(bowlerID), squads!inner(tournamentID)")
        .eq("squads.tournamentID", tournamentId)
        .eq("isDeleted", false),
      rpcOrDefault(
        supabase,
        "can_verify_entity",
        {
          p_tournament_id: tournamentId,
          p_entity_type: "payouts",
          p_entity_id: null,
        },
        false
      ),
      rpcOrDefault(
        supabase,
        "can_verify_entity",
        {
          p_tournament_id: tournamentId,
          p_entity_type: "results",
          p_entity_id: null,
        },
        false
      ),
      rpcOrDefault(
        supabase,
        "can_verify_entity",
        {
          p_tournament_id: tournamentId,
          p_entity_type: "tournament",
          p_entity_id: null,
        },
        false
      ),
      rpcOrDefault(
        supabase,
        "get_unverification_path",
        { p_tournament_id: tournamentId, p_entity_type: "averages" },
        []
      ),
      rpcOrDefault(
        supabase,
        "get_unverification_path",
        { p_tournament_id: tournamentId, p_entity_type: "scores" },
        []
      ),
      rpcOrDefault(
        supabase,
        "get_unverification_path",
        { p_tournament_id: tournamentId, p_entity_type: "payouts" },
        []
      ),
      rpcOrDefault(
        supabase,
        "get_unverification_path",
        { p_tournament_id: tournamentId, p_entity_type: "results" },
        []
      ),
    ]);

  if (statesResult.error) throw statesResult.error;
  if (teamsResult.error) {
    console.warn("Could not fetch active teams for verification:", teamsResult.error);
  }

  const teams = teamsResult.data || [];
  const activeBowlerIds = [
    ...new Set(
      teams.flatMap(
        (team) =>
          team.teamassignments?.map((assignment) => assignment.bowlerID) || []
      )
    ),
  ];

  return buildVerificationStatus({
    tournamentId,
    states: statesResult.data || [],
    activeTeamIds: teams.map((team) => team.id),
    activeBowlerIds,
    canVerifyPayouts: payouts,
    canVerifyResults: results,
    canVerifyTournament: tournament,
    unverificationPathAverages: avgPath,
    unverificationPathScores: scoresPath,
    unverificationPathPayouts: payoutsPath,
    unverificationPathResults: resultsPath,
  });
};

export const loadTournamentVerification = async (supabase, tournamentId) => {
  const { data, error } = await supabase.rpc(
    "get_tournament_verification_bundle",
    { p_tournament_id: tournamentId }
  );
  if (!error && data) return data;
  if (error) {
    console.warn(
      "get_tournament_verification_bundle unavailable, using table reads:",
      error
    );
  }
  return loadTournamentVerificationFromTables(supabase, tournamentId);
};
